<template>
  <v-app id="inspire">
    <v-overlay :value="$store.getters.layoutIsLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-main class="grey lighten-3">
      <v-container>
        <v-row>
          <v-col>
            <v-sheet
                min-height="100vh"
                rounded="lg"
            >
              <v-alert prominent type="error" v-if="isMailNotFound" class="text-center" :icon="false">
                {{ $t('trackPage.alerts.notFound') }}
              </v-alert>
              <v-alert prominent type="success" v-if="mail" class="text-center" :icon="false">
                {{ $t('trackPage.alerts.found') }}
              </v-alert>

              <v-row>
                <v-col md="3" lg="4">
                  <v-btn color="primary" text :to="{name: 'dashboard'}">{{ $t('trackPage.goHomeBtn') }}</v-btn>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <v-text-field v-model="search"
                                outlined
                                :placeholder="$t('trackPage.searchPlaceholder')"
                                append-outer-icon="mdi-magnify"
                                @click:append-outer="onSearchMailEmited"
                                @keyup.enter="onSearchMailEmited"
                  />
                </v-col>
              </v-row>

              <v-card v-if="mail">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th class="text-left" style="width: 170px">
                        {{ $t('trackPage.table.time') }}
                      </th>
                      <th class="text-left">
                        {{ $t('trackPage.table.action') }}
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="history in mail.histories" :key="history.id">
                      <td><b>{{ history.createdOn.format('YYYY.MM.DD HH:mm:ss') }}</b></td>
                      <td>
                        <template v-if="history.historyType === historyTypeCreated">{{ $t('trackPage.history.created') }}</template>
                        <template v-if="history.historyType === historyTypePrinted">{{ $t('trackPage.history.printed') }}</template>
                        <template v-if="history.historyType === historyTypePostPacked">
                          <span v-html="$t('trackPage.history.postPacked', { postCode: history.postPackagePostCode })"></span>
                        </template>
                        <template v-if="history.historyType === historyTypeDeleteFromPostPackage">{{ $t('trackPage.history.deleteFromPostPackage') }}</template>
                        <template v-if="history.historyType === historyTypeSetResult">
                          <span v-html="$t('trackPage.history.setResult', { statusName: history.perform.performType.name, date: history.perform.performedOn.format('YYYY.MM.DD') })"></span>
                          <template v-if="history.perform.note && history.perform.note.length > 0">
                            <br/>
                            <span class="ml-5"><i>{{ history.perform.note }}</i></span>
                          </template>
                        </template>
                        <template v-if="history.historyType === historyTypeDeleteFromPostPackageAsOnCreationError">
                          {{ $t('trackPage.history.deleteOnError') }}
                        </template>
                        <template v-if="history.historyType === historyTypeDistributedToPostOffice">
                          {{ $t('trackPage.history.distributedToPost') }}
                        </template>
                        <template v-if="history.historyType === historyTypeTransferredToExternalClient">
                          {{ $t('trackPage.history.transferredToExternal') }}
                        </template>
                      </td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </v-sheet>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import MailTrackingService from '../services/mail.tracking.service'
import {
  LAYOUT_LOADING_START,
  LAYOUT_LOADING_STOP,
  LAYOUT_TITLE_CHANGED
} from "../store/modules/layout/actions";
import mappers from '../mappers';
import Moment from 'moment'
import _ from 'underscore'

export default {
  name: "TrackPage",
  data() {
    return {
      mail: null,
      isMailNotFound: false,

      search: '',

      historyTypeCreated: 0,
      historyTypePrinted: 1,
      historyTypePostPacked: 2,
      historyTypeDeleteFromPostPackage: 3,
      historyTypeSetResult: 4,
      historyTypeDeleteFromPostPackageAsOnCreationError: 5,
      historyTypeDistributedToPostOffice: 6,
      historyTypeTransferredToExternalClient: 7,
    }
  },
  methods: {
    onSearchMailEmited() {
      // clear empty fields
      while (this.search.indexOf(' ') > -1)
        this.search = this.search.replace(' ', '')

      if (this.search.length > 0) {
        if (this.search !== this.$route.params.uid)
          this.$router.push({name: this.$route.name, params: {uid: this.search}})
      }
    },

    loadMail() {
      this.search = this.$route.params.uid

      this.mail = null
      this.isMailNotFound = false

      this.$store.dispatch(LAYOUT_LOADING_START)

      MailTrackingService.get(this.$route.params.uid)
          .then(response => {
            if (response.data === null) {
              if (this.$route.params.uid.length !== ' ')
                this.isMailNotFound = true
            } else {
              this.mail = {
                region: mappers.mapRegion(response.data.Region),
                area: mappers.mapArea(response.data.Area),
                receiver: response.data.Receiver,
                address: response.data.Address,
                pagesCount: response.data.PagesCount,
                uid: response.data.Uid,
                createdOn: Moment(response.data.CreatedOn, 'YYYY-MM-DD HH-mm-ss'),
                histories: _.map(response.data.Histories, h => {
                  return {
                    id: h.Id,
                    account: h.Account ? h.Account.Name : null,
                    createdOn: Moment(h.CreatedOn, 'YYYY-MM-DD HH-mm-ss'),
                    historyType: h.HistoryType,
                    postPackagePostCode: h.PostPackagePostCode,
                    perform: h.Perform ? {
                      createdOn: Moment(h.Perform.CreatedOn, 'YYYY-MM-DD HH-mm-ss'),
                      note: h.Perform.Note,
                      performType: mappers.mapPerformType(h.Perform.Type),
                      performedOn: Moment(h.Perform.PerformedOn, 'YYYY-MM-DD HH-mm-ss'),
                    } : null
                  }
                })
              }
            }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    }
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('trackPage.pageTitle'))
    this.loadMail()
  },
  watch: {
    "$route": function () {
      this.loadMail()
    }
  }
}
</script>